import React, { createContext, useContext, useState, useCallback } from 'react';
import { logger } from '../utils';

interface User {
  sub: string;
  email: string;
  role: 'tedy-admin' | 'tedy-partner' | 'tedy-student';
  name?: string;
  organization?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  login: (accessToken: string, idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  token: string | null;
  user: User | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => {
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      logger.info('AUTH', 'Restored auth token from storage');
      return storedToken;
    }
    return null;
  });

  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem('auth_user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        logger.info('AUTH', 'Restored user from storage');
        return parsedUser;
      } catch (error) {
        logger.error('AUTH', 'Error parsing stored user', { error });
        localStorage.removeItem('auth_user');
        return null;
      }
    }
    return null;
  });

  const login = useCallback(async (accessToken: string, idToken: string) => {
    try {
      logger.info('AUTH', 'Processing login');

      // Parse ID token for user info
      const [header, payload, signature] = idToken.split('.');
      if (!header || !payload || !signature) {
        throw new Error('Invalid ID token format');
      }

      // Add padding to base64 string if needed
      const base64Payload = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padding = '='.repeat((4 - base64Payload.length % 4) % 4);
      const paddedPayload = base64Payload + padding;

      // Decode ID token payload
      const decodedPayload = JSON.parse(atob(paddedPayload));
      logger.debug('AUTH', 'ID token payload', decodedPayload);

      // Validate token expiration
      const now = Math.floor(Date.now() / 1000);
      if (decodedPayload.exp && decodedPayload.exp < now) {
        throw new Error('ID token has expired');
      }

      // For now, hardcode role for testing
      const role: User['role'] = 'tedy-admin';

      // Create user object
      const user: User = {
        sub: decodedPayload.sub,
        email: decodedPayload.email,
        role,
        name: decodedPayload.name,
        organization: decodedPayload.organization
      };

      // Store tokens and user info
      localStorage.setItem('auth_token', accessToken);
      localStorage.setItem('auth_user', JSON.stringify(user));
      setToken(accessToken);
      setUser(user);

      logger.info('AUTH', 'Login successful', { role: user.role });
    } catch (error) {
      logger.error('AUTH', 'Login failed', { error });
      // Clean up any partial state
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      setToken(null);
      setUser(null);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    logger.info('AUTH', 'Processing logout');
    setToken(null);
    setUser(null);
  }, []);

  const value = {
    isAuthenticated: !!token,
    login,
    logout,
    token,
    user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
